import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert, Modal } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { FaLinkedin, FaUser, FaBullseye, FaIndustry, FaCheckCircle, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import './OnboardingForm.css';

const OnboardingForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // User/Company Information
    fullName: '',
    email: '',
    linkedinProfileUrl: '',
    linkedinCompanyPageUrl: '',
    
    // Preferences (React Select format)
    jobTitles: [],
    targetAudience: [],
    targetCountries: [],
    industries: [],
    primaryGoals: [],
    
    // Additional fields
    userType: 'individual', // 'individual' or 'company'
    phoneNumber: '',
    companySize: '',
    website: '',
    
    // Business-specific fields (only for company type)
    businessName: '',
    businessDescription: '',
    primaryServices: [], // Changed to array for multi-select
    brandTone: null,
    writingStyle: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLinkedInModal, setShowLinkedInModal] = useState(false);
  const [linkedInConnected, setLinkedInConnected] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [userId, setUserId] = useState(null);
  const [billingPlan, setBillingPlan] = useState('Annually'); // 'Monthly' | 'Annually'

  // State for dropdown options (fetched from APIs)
  const [jobTitles, setJobTitles] = useState([]);
  const [targetAudienceList, setTargetAudienceList] = useState([]);
  const [countries, setCountries] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  // Fetch dropdown options from APIs (same as user form)
  const fetchJobTitles = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/jobtitles`);
      setJobTitles(response.data);
    } catch (error) {
      console.error("Error fetching job titles:", error);
    }
  };

  const fetchTargetAudience = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/targetaudience`);
      setTargetAudienceList(response.data);
    } catch (error) {
      console.error("Error fetching target audience:", error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchIndustries = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/industries`);
      setIndustries(response.data);
    } catch (error) {
      console.error("Error fetching industries:", error);
    }
  };

  // Load all dropdown options on component mount
  useEffect(() => {
    const loadOptions = async () => {
      setIsLoadingOptions(true);
      await Promise.all([
        fetchJobTitles(),
        fetchTargetAudience(),
        fetchCountries(),
        fetchIndustries()
      ]);
      setIsLoadingOptions(false);
    };
    loadOptions();
  }, []);

  // Handle URL parameter for step (when returning from LinkedIn callback or payment)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const stepParam = urlParams.get('step');
    if (stepParam && !isNaN(stepParam)) {
      const stepNumber = parseInt(stepParam);
      if (stepNumber >= 1 && stepNumber <= 6) {
        setCurrentStep(stepNumber);
        
        // If returning to Step 5 or 6, try to restore form data from localStorage
        if (stepNumber === 5 || stepNumber === 6) {
          const savedFormData = localStorage.getItem('onboardingFormData');
          if (savedFormData) {
            try {
              const parsedData = JSON.parse(savedFormData);
              setFormData(parsedData);
              console.log(`Restored form data for Step ${stepNumber}:`, parsedData);
            } catch (error) {
              console.error('Error parsing saved form data:', error);
            }
          }
        }
      }
    }
  }, [location.search]);

  // Restore form data on component mount if we're on Step 6
  useEffect(() => {
    if (currentStep === 6) {
      const savedFormData = localStorage.getItem('onboardingFormData');
      if (savedFormData) {
        try {
          const parsedData = JSON.parse(savedFormData);
          setFormData(parsedData);
          console.log('Restored form data on mount for Step 6:', parsedData);
        } catch (error) {
          console.error('Error parsing saved form data on mount:', error);
        }
      }
    }
  }, [currentStep]);

  // Handle shareable onboarding link parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');
    const email = urlParams.get('email');
    const name = urlParams.get('name');
    const company = urlParams.get('company');
    const source = urlParams.get('source');
    
    if (token) {
      console.log('🔗 Shareable onboarding link detected:', {
        token,
        email,
        name,
        company,
        source
      });
      
      // Pre-fill form data if parameters are provided
      if (email || name || company) {
        setFormData(prevData => ({
          ...prevData,
          email: email ? decodeURIComponent(email) : prevData.email,
          fullName: name ? decodeURIComponent(name) : prevData.fullName,
          userType: company ? 'company' : prevData.userType
        }));
        
        // Show a welcome message for shareable link users
        if (source === 'email') {
          Swal.fire({
            icon: 'info',
            title: 'Welcome to KnackVibes!',
            text: 'You\'ve been invited to join KnackVibes. Let\'s get you set up!',
            confirmButtonText: 'Get Started',
            confirmButtonColor: '#2e3192'
          });
        }
      }
    }
  }, [location.search]);

  // Transform API data to dropdown options (same format as user form)
  const jobTitleOptions = jobTitles?.map(j => ({
    value: j.JobTitleID,
    label: j.JobTitle
  }));

  const targetAudienceOptions = targetAudienceList?.map(a => ({
    value: a.AudienceID,
    label: a.AudienceType
  }));

  const countryOptions = countries?.map(c => ({
    value: c.CountryCode,
    label: c.CountryName
  }));

  const industryOptions = industries?.map(i => ({
    value: i.IndustryID,
    label: i.IndustryName
  }));

  // Primary goals options (these might be static or could also come from an API)
  const primaryGoalsOptions = [
    { value: 'Brand Awareness', label: 'Brand Awareness' },
    { value: 'Attract Investors', label: 'Attract Investors' },
    { value: 'Networking', label: 'Networking' },
    { value: 'Hiring', label: 'Hiring' },
    { value: 'Thought Leadership', label: 'Thought Leadership' },
    { value: 'Lead Generation', label: 'Lead Generation' },
    { value: 'Sales Growth', label: 'Sales Growth' },
    { value: 'Partnership Development', label: 'Partnership Development' },
    { value: 'Market Expansion', label: 'Market Expansion' },
    { value: 'Customer Engagement', label: 'Customer Engagement' }
  ].sort((a, b) => a.label.localeCompare(b.label));

  // Brand tone options for business pages
  const brandToneOptions = [
    { value: 'Professional', label: 'Professional' },
    { value: 'Friendly', label: 'Friendly' },
    { value: 'Casual', label: 'Casual' },
    { value: 'Formal', label: 'Formal' },
    { value: 'Humorous', label: 'Humorous' },
    { value: 'Inspirational', label: 'Inspirational' },
    { value: 'Authoritative', label: 'Authoritative' },
    { value: 'Conversational', label: 'Conversational' },
    { value: 'Spiritual', label: 'Spiritual' },
    { value: 'Devotional', label: 'Devotional' },
    { value: 'Reverent', label: 'Reverent' },
    { value: 'Traditional', label: 'Traditional' },
    { value: 'Sacred', label: 'Sacred' }
  ];

  // Writing style options for business pages
  const writingStyleOptions = [
    { value: 'Short & Punchy', label: 'Short & Punchy' },
    { value: 'Detailed & Informative', label: 'Detailed & Informative' },
    { value: 'Storytelling', label: 'Storytelling' },
    { value: 'Question-Based', label: 'Question-Based' },
    { value: 'List-Based', label: 'List-Based' },
    { value: 'Educational', label: 'Educational' },
    { value: 'News-Style', label: 'News-Style' },
    { value: 'Personal Anecdote', label: 'Personal Anecdote' },
    { value: 'Devotional', label: 'Devotional' },
    { value: 'Traditional', label: 'Traditional' },
    { value: 'Sacred', label: 'Sacred' },
    { value: 'Reverent', label: 'Reverent' }
  ];

  // Primary Services / Products options for business pages
  const primaryServicesOptions = [
    { value: 'Web Development', label: 'Web Development' },
    { value: 'Mobile App Development', label: 'Mobile App Development' },
    { value: 'Software Development', label: 'Software Development' },
    { value: 'SaaS Platform', label: 'SaaS Platform' },
    { value: 'Cloud Services', label: 'Cloud Services' },
    { value: 'IT Consulting', label: 'IT Consulting' },
    { value: 'Digital Marketing', label: 'Digital Marketing' },
    { value: 'Social Media Management', label: 'Social Media Management' },
    { value: 'Content Creation', label: 'Content Creation' },
    { value: 'SEO Services', label: 'SEO Services' },
    { value: 'E-commerce Solutions', label: 'E-commerce Solutions' },
    { value: 'Business Consulting', label: 'Business Consulting' },
    { value: 'Financial Services', label: 'Financial Services' },
    { value: 'Accounting Services', label: 'Accounting Services' },
    { value: 'Legal Services', label: 'Legal Services' },
    { value: 'HR Services', label: 'HR Services' },
    { value: 'Recruitment', label: 'Recruitment' },
    { value: 'Training & Education', label: 'Training & Education' },
    { value: 'Healthcare Services', label: 'Healthcare Services' },
    { value: 'Real Estate', label: 'Real Estate' },
    { value: 'Construction', label: 'Construction' },
    { value: 'Manufacturing', label: 'Manufacturing' },
    { value: 'Retail', label: 'Retail' },
    { value: 'Food & Beverage', label: 'Food & Beverage' },
    { value: 'Hospitality', label: 'Hospitality' },
    { value: 'Travel & Tourism', label: 'Travel & Tourism' },
    { value: 'Fitness & Wellness', label: 'Fitness & Wellness' },
    { value: 'Beauty & Cosmetics', label: 'Beauty & Cosmetics' },
    { value: 'Fashion & Apparel', label: 'Fashion & Apparel' },
    { value: 'Automotive', label: 'Automotive' },
    { value: 'Energy & Utilities', label: 'Energy & Utilities' },
    { value: 'Telecommunications', label: 'Telecommunications' },
    { value: 'Media & Entertainment', label: 'Media & Entertainment' },
    { value: 'Gaming', label: 'Gaming' },
    { value: 'Non-Profit', label: 'Non-Profit' },
    { value: 'Daily Pooja', label: 'Daily Pooja' },
    { value: 'Abhishekam', label: 'Abhishekam' },
    { value: 'Festivals', label: 'Festivals' },
    { value: 'Religious Ceremonies', label: 'Religious Ceremonies' },
    { value: 'Spiritual Guidance', label: 'Spiritual Guidance' },
    { value: 'Temple Visits', label: 'Temple Visits' },
    { value: 'Devotional Programs', label: 'Devotional Programs' },
    { value: 'Special Poojas', label: 'Special Poojas' },
    { value: 'Religious Education', label: 'Religious Education' },
    { value: 'Prasadam Distribution', label: 'Prasadam Distribution' },
    { value: 'Religious Counseling', label: 'Religious Counseling' },
    { value: 'Other', label: 'Other' }
  ].sort((a, b) => a.label.localeCompare(b.label));

  const steps = [
    { number: 1, title: 'Welcome', description: 'Introduction to KnackVibes' },
    { number: 2, title: 'Basic Info', description: 'User/Company Information' },
    { number: 3, title: 'Preferences', description: 'Target Audience & Goals' },
    { number: 4, title: 'LinkedIn', description: 'Connect LinkedIn Account' },
    { number: 5, title: 'Payment', description: 'Subscribe to KnackVibes' },
    { number: 6, title: 'Complete', description: 'Review & Submit' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 2:
        if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
        if (formData.userType === 'individual') {
          if (!formData.linkedinProfileUrl.trim()) newErrors.linkedinProfileUrl = 'LinkedIn Profile URL is required';
          else if (!formData.linkedinProfileUrl.includes('linkedin.com')) newErrors.linkedinProfileUrl = 'Please enter a valid LinkedIn URL';
        }
        if (formData.userType === 'company') {
          if (!formData.linkedinCompanyPageUrl.trim()) newErrors.linkedinCompanyPageUrl = 'LinkedIn Company Page URL is required';
          else if (!formData.linkedinCompanyPageUrl.includes('linkedin.com')) newErrors.linkedinCompanyPageUrl = 'Please enter a valid LinkedIn URL';
        }
        break;
      
      case 3:
        if (formData.jobTitles.length === 0) newErrors.jobTitles = 'Please select at least one job title';
        if (formData.targetAudience.length === 0) newErrors.targetAudience = 'Please select at least one target audience';
        if (formData.targetCountries.length === 0) newErrors.targetCountries = 'Please select at least one country';
        if (formData.industries.length === 0) newErrors.industries = 'Please select at least one industry';
        if (formData.primaryGoals.length === 0) newErrors.primaryGoals = 'Please select at least one primary goal';
        // Business-specific validations
        if (formData.userType === 'company') {
          if (!formData.businessDescription.trim()) newErrors.businessDescription = 'Business description is required';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Submit form data to backend (keep the {value, label} format)
      const submitData = {
        ...formData
      };

      console.log('Submitting onboarding data:', submitData);

      // Submit form data to backend
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/onboarding`, submitData);
      
      if (response.data.success) {
        // Generate 5 prompts based on user preferences for onboarding users
        const userId = response.data.userId;
        if (userId) {
          try {
            const jobTitlesText = formData.jobTitles.length > 0 
              ? formData.jobTitles.map(j => j.label).join(', ')
              : 'professionals';
            const audienceText = formData.targetAudience.length > 0 
              ? formData.targetAudience.map(a => a.label).join(', ')
              : 'your audience';
            const countriesText = formData.targetCountries.length > 0 
              ? formData.targetCountries.map(c => c.label).join(', ')
              : 'your region';
            const industryText = formData.industries.length > 0 
              ? formData.industries.map(i => i.label).join(', ')
              : 'your industry';
            const goalText = formData.primaryGoals.length > 0 
              ? formData.primaryGoals.map(g => g.label).join(', ')
              : 'growth and engagement';

            const generatedPrompts = [
              `Share a quick tip for ${jobTitlesText} in ${industryText} to achieve ${goalText}.`,
              `Ask a question to ${audienceText} about a common challenge in ${industryText}.`,
              `Post a short story of a win related to ${goalText} from ${countriesText}.`,
              `Create a checklist: 5 steps ${jobTitlesText} can take this week toward ${goalText}.`,
              `Recommend a tool or resource helping ${audienceText} with ${goalText} in ${industryText}.`
            ];

            // Save the generated prompts to database
            for (const promptText of generatedPrompts) {
              await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/saveprompts`, {
                UserID: userId,
                PromptData: promptText,
                PromptID: null
              });
            }

            console.log('Successfully generated and saved 5 prompts for onboarding user');
          } catch (promptError) {
            console.error('Error saving prompts for onboarding user:', promptError);
          }
        }

        Swal.fire({
          icon: 'success',
          title: 'Account Created Successfully!',
          text: `Your account has been created! Check your email (${formData.email}) for login credentials. You can now log in and start using KnackVibes.`,
          confirmButtonText: 'Great!'
        });
        
        // Reset form or redirect
        setCurrentStep(1);
        setFormData({
          fullName: '',
          email: '',
          linkedinProfileUrl: '',
          linkedinCompanyPageUrl: '',
          jobTitles: [],
          targetAudience: [],
          targetCountries: [],
          industries: [],
          primaryGoals: [],
          userType: 'individual',
          phoneNumber: '',
          companySize: '',
          website: '',
          businessName: '',
          businessDescription: '',
          primaryServices: [],
          brandTone: null,
          writingStyle: null
        });
        
        // Clean up saved form data
        localStorage.removeItem('onboardingFormData');
      }
    } catch (error) {
      console.error('Onboarding submission error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Submission Failed',
        text: 'There was an error submitting your information. Please try again.',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLinkedInAuth = () => {
    // First, we need to create the user account, then redirect to LinkedIn auth
    // We'll store the form data temporarily and create the account
    createAccountAndRedirectToLinkedIn();
  };

  const createAccountAndRedirectToLinkedIn = async () => {
    try {
      setIsSubmitting(true);
      
      // Submit form data to create account first
      const submitData = {
        ...formData
      };

      console.log('Creating account before LinkedIn auth:', submitData);

      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/onboarding`, submitData);
      
      if (response.data.success) {
        // Store user info for LinkedIn auth
        const { userId, userType } = response.data;
        
        // Generate and save 5 prompts based on user preferences
        try {
          const jobTitlesText = formData.jobTitles.length > 0 
            ? formData.jobTitles.map(j => j.label).join(', ')
            : 'professionals';
          const audienceText = formData.targetAudience.length > 0 
            ? formData.targetAudience.map(a => a.label).join(', ')
            : 'your audience';
          const countriesText = formData.targetCountries.length > 0 
            ? formData.targetCountries.map(c => c.label).join(', ')
            : 'your region';
          const industryText = formData.industries.length > 0 
            ? formData.industries.map(i => i.label).join(', ')
            : 'your industry';
          const goalText = formData.primaryGoals.length > 0 
            ? formData.primaryGoals.map(g => g.label).join(', ')
            : 'growth and engagement';

          const generatedPrompts = [
            `Share a quick tip for ${jobTitlesText} in ${industryText} to achieve ${goalText}.`,
            `Ask a question to ${audienceText} about a common challenge in ${industryText}.`,
            `Post a short story of a win related to ${goalText} from ${countriesText}.`,
            `Create a checklist: 5 steps ${jobTitlesText} can take this week toward ${goalText}.`,
            `Recommend a tool or resource helping ${audienceText} with ${goalText} in ${industryText}.`
          ];

          // Save the generated prompts to database
          for (const promptText of generatedPrompts) {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/saveprompts`, {
              UserID: userId,
              PromptData: promptText,
              PromptID: null
            });
          }

          console.log('Successfully generated and saved 5 prompts for LinkedIn auth user');
        } catch (promptError) {
          console.error('Error saving prompts for LinkedIn auth user:', promptError);
        }
        
        // Store user info in localStorage for LinkedIn auth
        localStorage.setItem('tempUserId', userId);
        localStorage.setItem('tempUserType', userType);
        localStorage.setItem('tempFormData', JSON.stringify(formData));
        
        // Redirect to LinkedIn authorization
        const loginType = userType === 'company' ? 'Page' : 'User';
        // Use the standard LinkedIn callback URL that's likely registered in your LinkedIn app
        const callbackUrl = encodeURIComponent(`${process.env.REACT_APP_API_BASE_URL}/api/linkedin/redirect`);
        const linkedinAuthorizeUrl = `${process.env.REACT_APP_API_BASE_URL}/api/linkedin/authorize?UserID=${userId}&loginType=${loginType}&scopes=w_member_social&callbackUrl=${callbackUrl}&clientId=onboarding`;
        
        // Redirect to LinkedIn
        window.location.href = linkedinAuthorizeUrl;
      }
    } catch (error) {
      console.error('Error creating account:', error);
      Swal.fire({
        icon: 'error',
        title: 'Account Creation Failed',
        text: error.response?.data?.message || 'There was an error creating your account. Please try again.',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayment = () => {
    // Get the userId from onboardingFormData (set during LinkedIn callback)
    const onboardingData = JSON.parse(localStorage.getItem('onboardingFormData') || '{}');
    const userId = onboardingData.userId;
    
    if (!userId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'User ID not found. Please restart the onboarding process.',
        confirmButtonText: 'OK'
      });
      return;
    }
    
    // Determine amount based on selected plan
    const amount = billingPlan === 'Annually' ? 1000 : 100; // USD

    // Store onboarding data for payment page
    localStorage.setItem('onboardingPaymentData', JSON.stringify({
      userId: parseInt(userId),
      userName: formData.fullName,
      userEmail: formData.email,
      amount: amount,
      type: 'onboarding',
      plan: billingPlan,
      currency: 'USD',
      returnUrl: '/onboarding?step=6' // Return to step 6 after payment
    }));
    
    // Navigate to onboarding payment page
    navigate('/onboarding-payment');
  };

  const handleCompleteOnboarding = () => {
    // Navigate to login page
    navigate('/Login');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="text-center">
            <h2 className="mb-4">Welcome to KnackVibes! 🚀</h2>
            <p className="lead mb-4">
              Your AI-powered LinkedIn automation partner. Let's get you set up in just a few steps.
            </p>
            
            <div className="row mt-5">
              <div className="col-md-4 mb-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body text-center">
                    <FaUser className="mb-3" size={48} style={{color: '#2e3192'}} />
                    <h5>AI-Generated Content</h5>
                    <p className="text-muted">We create engaging posts tailored to your audience</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body text-center">
                    <FaBullseye className="text-success mb-3" size={48} />
                    <h5>Smart Scheduling</h5>
                    <p className="text-muted">Optimal timing for maximum engagement</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body text-center">
                    <FaIndustry className="text-warning mb-3" size={48} />
                    <h5>Trending Feeds</h5>
                    <p className="text-muted">Stay ahead with industry insights</p>
                  </div>
                </div>
              </div>
            </div>

            <Alert variant="info" className="mt-4" style={{ border: '1px solid #cfe2ff', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
              <div className="text-center mb-3" style={{fontWeight: 800, color: '#2e3192', letterSpacing: 0.2}}>What you'll get:</div>
              <Row className="mt-2" style={{ textAlign: 'left' }}>
                <Col md={6}>
                  <ul className="mb-0 ps-1" style={{ listStyle: 'none', paddingLeft: 0 }}>
                    <li className="mb-2 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                      <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                      <span>AI-Powered Content, Crafted with a Human Touch</span>
                    </li>
                    <li className="mb-2 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                      <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                      <span>Smart Scheduling, Maximum Visibility</span>
                    </li>
                    <li className="mb-2 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                      <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                      <span>Profile Enhancement Made Simple</span>
                    </li>
                    <li className="mb-0 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                      <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                      <span>Stay Ahead with Daily Trending Posts</span>
                    </li>
                  </ul>
                </Col>
                <Col md={6}>
                  <div className="h-100 ps-md-4 mt-3 mt-md-0" style={{ borderLeft: '1px dashed #c2d4ff' }}>
                    <ul className="mb-0 ps-1" style={{ listStyle: 'none', paddingLeft: 0 }}>
                      <li className="mb-2 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                        <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                        <span>Themed Challenges that Inspire Action</span>
                      </li>
                      <li className="mb-2 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                        <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                        <span>Celebrate Every Occasion with Impact</span>
                      </li>
                      <li className="mb-2 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                        <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                        <span>Consistent Presence, Effortless Engagement</span>
                      </li>
                      <li className="mb-0 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                        <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                        <span>Always-On Support, Just a Message Away</span>
                      </li>
                    </ul>
                  </div>
                </Col>
              </Row>
            </Alert>
          </div>
        );

      case 2:
        return (
          <div>
            <h4 className="mb-4">Basic Information</h4>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Account Type</Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      label="Personal Branding"
                      name="userType"
                      value="individual"
                      checked={formData.userType === 'individual'}
                      onChange={(e) => handleInputChange('userType', e.target.value)}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Business Branding"
                      name="userType"
                      value="company"
                      checked={formData.userType === 'company'}
                      onChange={(e) => handleInputChange('userType', e.target.value)}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {formData.userType === 'individual' ? 'Full Name' : 'Company Name'} *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    isInvalid={!!errors.fullName}
                    placeholder={formData.userType === 'individual' ? 'Enter your full name' : 'Enter company name'}
                  />
                  <Form.Control.Feedback type="invalid">{errors.fullName}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address *</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    isInvalid={!!errors.email}
                    placeholder="Enter your email address"
                  />
                  <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              {formData.userType === 'individual' && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>LinkedIn Profile URL *</Form.Label>
                    <Form.Control
                      type="url"
                      value={formData.linkedinProfileUrl}
                      onChange={(e) => handleInputChange('linkedinProfileUrl', e.target.value)}
                      isInvalid={!!errors.linkedinProfileUrl}
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                    <Form.Control.Feedback type="invalid">{errors.linkedinProfileUrl}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              )}
              {formData.userType === 'company' && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>LinkedIn Company Page URL *</Form.Label>
                    <Form.Control
                      type="url"
                      value={formData.linkedinCompanyPageUrl}
                      onChange={(e) => handleInputChange('linkedinCompanyPageUrl', e.target.value)}
                      isInvalid={!!errors.linkedinCompanyPageUrl}
                      placeholder="https://linkedin.com/company/yourcompany"
                    />
                    <Form.Control.Feedback type="invalid">{errors.linkedinCompanyPageUrl}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              )}
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    placeholder="Enter your phone number"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Website</Form.Label>
                  <Form.Control
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://yourwebsite.com"
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        );

      case 3:
        return (
          <div>
            <h4 className="mb-4">Preferences & Targeting</h4>
            
            {isLoadingOptions ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading options...</p>
              </div>
            ) : (
              <>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Job Titles You Want to Target *</Form.Label>
                  <Select
                    isMulti
                    options={jobTitleOptions}
                    value={formData.jobTitles}
                    onChange={(selectedOptions) => handleInputChange('jobTitles', selectedOptions || [])}
                    placeholder="Select job titles..."
                    className={errors.jobTitles ? 'is-invalid' : ''}
                    classNamePrefix="react-select"
                  />
                  {errors.jobTitles && <div className="invalid-feedback d-block">{errors.jobTitles}</div>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Target Audience *</Form.Label>
                  <Select
                    isMulti
                    options={targetAudienceOptions}
                    value={formData.targetAudience}
                    onChange={(selectedOptions) => handleInputChange('targetAudience', selectedOptions || [])}
                    placeholder="Select target audience..."
                    className={errors.targetAudience ? 'is-invalid' : ''}
                    classNamePrefix="react-select"
                  />
                  {errors.targetAudience && <div className="invalid-feedback d-block">{errors.targetAudience}</div>}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Target Countries *</Form.Label>
                  <Select
                    isMulti
                    options={countryOptions}
                    value={formData.targetCountries}
                    onChange={(selectedOptions) => handleInputChange('targetCountries', selectedOptions || [])}
                    placeholder="Select countries..."
                    className={errors.targetCountries ? 'is-invalid' : ''}
                    classNamePrefix="react-select"
                  />
                  {errors.targetCountries && <div className="invalid-feedback d-block">{errors.targetCountries}</div>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Industries of Interest *</Form.Label>
                  <Select
                    isMulti
                    options={industryOptions}
                    value={formData.industries}
                    onChange={(selectedOptions) => handleInputChange('industries', selectedOptions || [])}
                    placeholder="Select industries..."
                    className={errors.industries ? 'is-invalid' : ''}
                    classNamePrefix="react-select"
                  />
                  {errors.industries && <div className="invalid-feedback d-block">{errors.industries}</div>}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Primary Goals on LinkedIn *</Form.Label>
                  <Select
                    isMulti
                    options={primaryGoalsOptions}
                    value={formData.primaryGoals}
                    onChange={(selectedOptions) => handleInputChange('primaryGoals', selectedOptions || [])}
                    placeholder="Select primary goals..."
                    className={errors.primaryGoals ? 'is-invalid' : ''}
                    classNamePrefix="react-select"
                  />
                  {errors.primaryGoals && <div className="invalid-feedback d-block">{errors.primaryGoals}</div>}
                </Form.Group>
              </Col>
            </Row>

            {/* Business-specific fields - only shown for company type */}
            {formData.userType === 'company' && (
              <>
                <hr className="my-4" />
                <h5 className="mb-3" style={{ color: '#2e3192' }}>Business Information</h5>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Business Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.businessName}
                        onChange={(e) => handleInputChange('businessName', e.target.value)}
                        placeholder="Enter your business name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Brand Tone</Form.Label>
                      <Select
                        options={brandToneOptions}
                        value={formData.brandTone}
                        onChange={(selectedOption) => handleInputChange('brandTone', selectedOption)}
                        placeholder="Select brand tone"
                        isClearable
                        classNamePrefix="react-select"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Primary Services</Form.Label>
                      <Select
                        isMulti
                        options={primaryServicesOptions}
                        value={formData.primaryServices}
                        onChange={(selectedOptions) => handleInputChange('primaryServices', selectedOptions || [])}
                        placeholder="Select services..."
                        isClearable
                        classNamePrefix="react-select"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Writing Style</Form.Label>
                      <Select
                        options={writingStyleOptions}
                        value={formData.writingStyle}
                        onChange={(selectedOption) => handleInputChange('writingStyle', selectedOption)}
                        placeholder="Select writing style"
                        isClearable
                        classNamePrefix="react-select"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Business Description *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={formData.businessDescription}
                        onChange={(e) => handleInputChange('businessDescription', e.target.value)}
                        placeholder="Describe your business, what you do, and your core values..."
                        isInvalid={!!errors.businessDescription}
                      />
                      <Form.Control.Feedback type="invalid">{errors.businessDescription}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
              </>
            )}
          </div>
        );

      case 4:
        return (
          <div className="text-center">
            <h4 className="mb-4">Connect Your LinkedIn</h4>
            
            <div className="mb-4">
              <FaLinkedin size={64} className="mb-3" style={{color: '#2e3192'}} />
              <p className="lead">
                Connect your LinkedIn account to complete your setup
              </p>
            </div>

            <Alert variant="info" className="mb-4">
              <strong>🔒 Secure Process:</strong> We'll create your account first, then redirect you to LinkedIn's official login page. 
              After authorization, you'll be redirected back to complete your setup.
            </Alert>

            <div className="row mb-4">
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <div className="text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '50px', height: '50px', backgroundColor: '#2e3192'}}>
                      <span className="fw-bold">1</span>
                    </div>
                    <h6>Create Account</h6>
                    <p className="text-muted small">We'll create your KnackVibes account</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <div className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '50px', height: '50px'}}>
                      <span className="fw-bold">2</span>
                    </div>
                    <h6>LinkedIn Login</h6>
                    <p className="text-muted small">Redirect to LinkedIn for authorization</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <div className="bg-warning text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '50px', height: '50px'}}>
                      <span className="fw-bold">3</span>
                    </div>
                    <h6>Complete Setup</h6>
                    <p className="text-muted small">Return to complete your onboarding</p>
                  </div>
                </div>
              </div>
            </div>

            <Button 
              variant="primary" 
              size="lg" 
              onClick={handleLinkedInAuth}
              disabled={isSubmitting}
              style={{backgroundColor: '#2e3192', borderColor: '#2e3192'}}
              className="mb-3"
            >
              <FaLinkedin className="me-2" />
              {isSubmitting ? 'Creating Account...' : 'Connect LinkedIn & Complete Setup'}
            </Button>
            
            <p className="text-muted">
              This will create your account and redirect you to LinkedIn for authorization.
            </p>
          </div>
        );

      case 5:
        return (
          <div className="text-center">
            <h4 className="mb-4">Subscribe to KnackVibes</h4>
            
            <Card className="mb-4">
              <Card.Body className="p-4">
                <div className="mb-4">
                  <div className="d-flex justify-content-center align-items-center mb-3">
                    <div
                      className="toggle-container d-flex border rounded-pill"
                      style={{ overflow: 'hidden' }}
                    >
                      <div
                        className={`px-4 py-2 ${billingPlan === 'Monthly' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                        style={{ cursor: 'pointer', fontWeight: 600 }}
                        onClick={() => setBillingPlan('Monthly')}
                      >
                        Monthly
                      </div>
                      <div
                        className={`px-4 py-2 ${billingPlan === 'Annually' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                        style={{ cursor: 'pointer', fontWeight: 600 }}
                        onClick={() => setBillingPlan('Annually')}
                      >
                        Annually
                      </div>
                    </div>
                  </div>
                  <h2 className="text-primary mb-2">{billingPlan === 'Annually' ? '$1,000/year' : '$100/month'}</h2>
                  <p className="text-muted mb-4">Unlock the full power of KnackVibes with our premium subscription</p>
                </div>
                
                <div className="mb-4">
                  <h5>What's Included:</h5>
                  <Row className="mt-2" style={{ textAlign: 'left' }}>
                    <Col md={6}>
                      <ul className="mb-0 ps-1" style={{ listStyle: 'none', paddingLeft: 0 }}>
                        <li className="mb-2 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                          <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                          <span>AI-Powered Content, Crafted with a Human Touch</span>
                        </li>
                        <li className="mb-2 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                          <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                          <span>Smart Scheduling, Maximum Visibility</span>
                        </li>
                        <li className="mb-2 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                          <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                          <span>Profile Enhancement Made Simple</span>
                        </li>
                        <li className="mb-0 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                          <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                          <span>Stay Ahead with Daily Trending Posts</span>
                        </li>
                      </ul>
                    </Col>
                    <Col md={6}>
                      <ul className="mb-0 ps-1" style={{ listStyle: 'none', paddingLeft: 0 }}>
                        <li className="mb-2 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                          <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                          <span>Themed Challenges that Inspire Action</span>
                        </li>
                        <li className="mb-2 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                          <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                          <span>Celebrate Every Occasion with Impact</span>
                        </li>
                        <li className="mb-2 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                          <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                          <span>Consistent Presence, Effortless Engagement</span>
                        </li>
                        <li className="mb-0 d-flex align-items-start" style={{ color: '#1f2a44', fontWeight: 600 }}>
                          <FaCheckCircle className="me-2 mt-1" style={{ color: '#2e3192' }} />
                          <span>Always-On Support, Just a Message Away</span>
                        </li>
                      </ul>
                    </Col>
                  </Row>
                </div>
                
                {billingPlan === 'Annually' ? (
                  <Alert variant="info" className="text-start">
                    <strong>Annual Subscription:</strong> $1,000 billed yearly. Cancel anytime. <span className="text-success fw-bold">(2 months free)</span>
                  </Alert>
                ) : (
                  <Alert variant="info" className="text-start">
                    <strong>Monthly Subscription:</strong> $100 billed monthly. Cancel anytime.
                  </Alert>
                )}
              </Card.Body>
            </Card>
            
            <Button 
              variant="primary" 
              size="lg" 
              onClick={handlePayment}
              disabled={isProcessingPayment}
              style={{backgroundColor: '#2e3192', borderColor: '#2e3192'}}
              className="mb-3"
            >
              {isProcessingPayment 
                ? 'Processing Payment...' 
                : billingPlan === 'Annually' 
                  ? 'Subscribe Now – $1,000/year' 
                  : 'Subscribe Now – $100/month'}
            </Button>
            
            <p className="text-muted">
              Secure payment processing. Your account will be activated immediately after payment.
            </p>
          </div>
        );

      case 6:
        // Debug: Log form data to see what's available
        console.log('Step 6 - Current formData:', formData);
        console.log('Step 6 - localStorage onboardingFormData:', localStorage.getItem('onboardingFormData'));
        
        return (
          <div>
            <h4 className="mb-4">Review Your Information</h4>
            
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Basic Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>Name:</strong> {formData.fullName}</p>
                    <p><strong>Email:</strong> {formData.email}</p>
                    <p><strong>Account Type:</strong> {formData.userType === 'individual' ? 'Personal Branding' : 'Business Branding'}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>LinkedIn Profile:</strong> <a href={formData.linkedinProfileUrl} target="_blank" rel="noopener noreferrer">{formData.linkedinProfileUrl}</a></p>
                    {formData.linkedinCompanyPageUrl && (
                      <p><strong>Company Page:</strong> <a href={formData.linkedinCompanyPageUrl} target="_blank" rel="noopener noreferrer">{formData.linkedinCompanyPageUrl}</a></p>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Preferences</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>Job Titles:</strong> {formData.jobTitles.map(item => item.label).join(', ')}</p>
                    <p><strong>Target Audience:</strong> {formData.targetAudience.map(item => item.label).join(', ')}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Countries:</strong> {formData.targetCountries.map(item => item.label).join(', ')}</p>
                    <p><strong>Industries:</strong> {formData.industries.map(item => item.label).join(', ')}</p>
                  </Col>
                </Row>
                <p><strong>Primary Goals:</strong> {formData.primaryGoals.map(item => item.label).join(', ')}</p>
              </Card.Body>
            </Card>

            {formData.userType === 'company' && (formData.businessName || formData.businessDescription || formData.primaryServices || formData.brandTone || formData.writingStyle) && (
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Business Information</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      {formData.businessName && <p><strong>Business Name:</strong> {formData.businessName}</p>}
                      {formData.brandTone && <p><strong>Brand Tone:</strong> {formData.brandTone.label || formData.brandTone}</p>}
                      {formData.writingStyle && <p><strong>Writing Style:</strong> {formData.writingStyle.label || formData.writingStyle}</p>}
                    </Col>
                    <Col md={6}>
                      {formData.primaryServices && formData.primaryServices.length > 0 && (
                        <p><strong>Primary Services:</strong> {formData.primaryServices.map(item => item.label || item).join(', ')}</p>
                      )}
                    </Col>
                  </Row>
                  {formData.businessDescription && (
                    <Row>
                      <Col md={12}>
                        <p><strong>Business Description:</strong></p>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{formData.businessDescription}</p>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            )}

            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Payment Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>Subscription:</strong> {(() => {
                      const pay = JSON.parse(localStorage.getItem('onboardingPaymentData') || '{}');
                      if (pay?.plan === 'Monthly') return '$100/month (Monthly)';
                      return '$1,000/year (Annually)';
                    })()}</p>
                    <p><strong>Payment Status:</strong> <span className="badge bg-success">Completed</span></p>
                    <p><strong>Account Status:</strong> <span className="badge bg-success">Active</span></p>
                  </Col>
                  <Col md={6}>
                    <p><strong>LinkedIn Connection:</strong> <span className="badge bg-success">Connected</span></p>
                    <p><strong>Account Type:</strong> {formData.userType === 'individual' ? 'Personal Branding' : 'Business Branding'}</p>
                    <p><strong>Verification:</strong> <span className="badge bg-success">Verified</span></p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>


            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Account Summary</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                      <p><strong>Email:</strong> {formData.email}</p>
                      <p><strong>Password:</strong> {(formData.fullName || '').replace(/\s+/g, '')}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Subscription Start:</strong> {new Date().toLocaleDateString()}</p>
                    <p><strong>Subscription End:</strong> {new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Alert variant="success" className="mt-3" style={{ backgroundColor: '#eaf8ef', borderColor: '#2fb673' }}>
              <h5 className="mb-3" style={{ color: '#1b4332', fontWeight: 800 }}>
                <FaCheckCircle className="me-2" style={{ color: '#2fb673' }}/>Setup Complete!
              </h5>
              <Row>
                <Col md={6}>
                  <ul className="mb-0 ps-1" style={{ listStyle: 'none', paddingLeft: 0 }}>
                    <li className="mb-2 d-flex align-items-start" style={{ color: '#163a2a', fontWeight: 600 }}>
                      <FaCheckCircle className="me-2 mt-1" style={{ color: '#2fb673' }} />
                      <span>Your account has been created and activated</span>
                    </li>
                    <li className="mb-2 d-flex align-items-start" style={{ color: '#163a2a', fontWeight: 600 }}>
                      <FaCheckCircle className="me-2 mt-1" style={{ color: '#2fb673' }} />
                      <span>LinkedIn account connected successfully</span>
                    </li>
                    <li className="mb-2 d-flex align-items-start" style={{ color: '#163a2a', fontWeight: 600 }}>
                      <FaCheckCircle className="me-2 mt-1" style={{ color: '#2fb673' }} />
                      <span>Payment processed</span>
                    </li>
                    <li className="mb-0 d-flex align-items-start" style={{ color: '#163a2a', fontWeight: 600 }}>
                      <FaCheckCircle className="me-2 mt-1" style={{ color: '#2fb673' }} />
                      <span>All preferences and settings saved</span>
                    </li>
                  </ul>
                </Col>
                <Col md={6}>
                  <ul className="mb-0 ps-1" style={{ listStyle: 'none', paddingLeft: 0 }}>
                    <li className="mb-2 d-flex align-items-start" style={{ color: '#163a2a', fontWeight: 600 }}>
                      <FaCheckCircle className="me-2 mt-1" style={{ color: '#2fb673' }} />
                      <span>You can now start using KnackVibes</span>
                    </li>
                    <li className="mb-2 d-flex align-items-start" style={{ color: '#163a2a', fontWeight: 600 }}>
                      <FaCheckCircle className="me-2 mt-1" style={{ color: '#2fb673' }} />
                      <span>Login to your dashboard to access all features</span>
                    </li>
                    <li className="mb-0 d-flex align-items-start" style={{ color: '#163a2a', fontWeight: 600 }}>
                      <FaCheckCircle className="me-2 mt-1" style={{ color: '#2fb673' }} />
                      <span>Start creating and scheduling your first posts</span>
                    </li>
                  </ul>
                </Col>
              </Row>
            </Alert>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <h4>Invalid Step</h4>
            <p>Please refresh the page and try again.</p>
          </div>
        );
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <Card className="shadow">
            <Card.Header className="text-white" style={{backgroundColor: '#2e3192'}}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-0">KnackVibes Onboarding</h3>
                  <p className="mb-0 text-white" style={{fontSize: '14px'}}>Step {currentStep} of {steps.length}: {steps[currentStep - 1].title}</p>
                </div>
                <div className="text-end">
                  <div className="progress" style={{width: '200px', height: '8px'}}>
                    <div 
                      className="progress-bar bg-success" 
                      style={{width: `${(currentStep / steps.length) * 100}%`, backgroundColor: '#28a745'}}
                    ></div>
                  </div>
                </div>
              </div>
            </Card.Header>
            
            <Card.Body className="p-4">
              {renderStepContent()}
            </Card.Body>
            
            <Card.Footer className="bg-light">
              <div className="d-flex justify-content-between">
                <Button 
                  variant="outline-secondary" 
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                >
                  <FaArrowLeft className="me-2" />
                  Previous
                </Button>
                
                {currentStep === 5 ? (
                  // Payment step - no next button, payment is handled within the step
                  <div></div>
                ) : currentStep < steps.length ? (
                  <Button 
                    variant="primary" 
                    onClick={handleNext}
                    className="custom-primary-btn"
                    style={{backgroundColor: '#2e3192 !important', borderColor: '#2e3192 !important'}}
                  >
                    Next
                    <FaArrowRight className="ms-2" />
                  </Button>
                ) : (
                  <Button 
                    variant="success" 
                    onClick={handleCompleteOnboarding}
                    style={{backgroundColor: '#2e3192', borderColor: '#2e3192'}}
                  >
                    Complete Onboarding
                  </Button>
                )}
              </div>
            </Card.Footer>
          </Card>
        </div>
      </div>

      {/* LinkedIn Authorization Modal */}
      <Modal show={showLinkedInModal} onHide={() => setShowLinkedInModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaLinkedin className="text-primary me-2" />
            LinkedIn Authorization
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <p className="lead">Ready to connect your LinkedIn account?</p>
            <p>This will allow KnackVibes to post content on your behalf.</p>
            
            <div className="alert alert-info">
              <strong>Security:</strong> We use LinkedIn's official OAuth system. 
              Your credentials are never stored on our servers.
            </div>
            
            <Button 
              variant="primary" 
              size="lg"
              style={{backgroundColor: '#2e3192', borderColor: '#2e3192'}}
              onClick={() => {
                // Redirect to LinkedIn OAuth
                window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/linkedin/authorize?UserID=${localStorage.getItem('UserID')}&loginType=User&clientId=onboarding`;
              }}
            >
              <FaLinkedin className="me-2" />
              Authorize with LinkedIn
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OnboardingForm;
